import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ShoppingCart, Trash2, Minus, ArrowRight, ShoppingBag, Wallet, Crown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { useCart } from '@/contexts/CartContext';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';

export function CartPreview() {
  const { t } = useTranslation();
  const { items, itemCount, subtotal, updateQuantity, removeCredit, removeVipUpgrade, removeItem, clearCart } = useCart();
  const { config } = useTheme();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat(config?.language || 'pt-BR', {
      style: 'currency',
      currency: config?.currency?.code || 'BRL',
    }).format(price);
  };

  if (!isAuthenticated) {
    return null;
  }

  if (items.length === 0) {
    return (
      <Card className="bg-card border-border">
        <CardContent className="p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
            <ShoppingBag className="w-8 h-8 text-muted-foreground" />
          </div>
          <h3 className="font-display font-bold text-lg text-foreground mb-2">
            {t('store.cart.empty_title')}
          </h3>
          <p className="text-sm text-muted-foreground">
            {t('store.cart.empty_desc')}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-card border-border">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-display flex items-center gap-2">
            <ShoppingCart className="w-5 h-5" />
            {t('store.cart.title')}
            <Badge variant="secondary">{itemCount}</Badge>
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={clearCart}
            className="text-destructive hover:text-destructive h-auto p-2"
            title={t('store.cart.clear')}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-3 max-h-[400px] overflow-y-auto">
        {items.map((item) => {
          if (item.type === 'VIP_UPGRADE') {
            const amount = item.customAmount ?? item.product?.price ?? 0;
            return (
              <div key="vip-upgrade" className="flex gap-3 p-3 rounded-lg bg-muted/30 border border-border/50">
                <div className="w-16 h-16 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Crown className="w-8 h-8 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-foreground line-clamp-1">{item.product?.name}</p>
                  <div className="flex items-center justify-between mt-2">
                    <p className="text-sm font-bold text-primary">{formatPrice(amount)}</p>
                    <Button variant="outline" size="icon" className="h-6 w-6" onClick={removeVipUpgrade} aria-label={t('store.vip.upgrade.remove')}>
                      <Minus className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          }
          if (item.type === 'CREDIT') {
            const amount = item.customAmount ?? item.product?.price ?? 0;
            return (
              <div
                key="credit-topup"
                className="flex gap-3 p-3 rounded-lg bg-muted/30 border border-border/50"
              >
                <div className="w-16 h-16 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Wallet className="w-8 h-8 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-foreground line-clamp-1">
                    {t('store.addCreditsDialog.title')}
                  </p>
                  <div className="flex items-center justify-between mt-2">
                    <p className="text-sm font-bold text-primary">{formatPrice(amount)}</p>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-6 w-6"
                      onClick={removeCredit}
                    >
                      <Minus className="w-3 h-3" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          }

          return (
            <div
              key={item.productId}
              className="flex gap-3 p-3 rounded-lg bg-muted/30 border border-border/50"
            >
              {/* Product Image */}
              <div className="w-16 h-16 rounded-md overflow-hidden flex-shrink-0">
                <img
                  src={item.product?.image}
                  alt={item.product?.name}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Product Info */}
              <div className="flex-1 min-w-0">
                <Link
                  to={item.product?.categorySlug ? `/loja/${item.product.categorySlug}/produto/${item.product.slug}` : `/loja/produto/${item.product?.slug}`}
                  className="text-sm font-semibold text-foreground hover:text-primary transition-colors line-clamp-1 block"
                >
                  {item.product?.name}
                </Link>
                <p className="text-xs text-muted-foreground line-clamp-1">
                  {item.product?.shortDescription}
                </p>

                {item.discountPercent && item.discountPercent > 0 && (
                  <Badge variant="default" className="mt-1 text-xs bg-success text-success-foreground">
                    {item.discountPercent}% OFF
                  </Badge>
                )}

                <div className="flex items-center justify-between mt-2">
                  <div className="flex flex-col">
                    {item.discountPercent && item.discountPercent > 0 ? (
                      <>
                        <p className="text-xs text-muted-foreground line-through">
                          {formatPrice(item.product?.price * item.quantity)}
                        </p>
                        <p className="text-sm font-bold text-success">
                          {formatPrice((item.priceWithDiscount ?? item.product?.price ?? 0) * item.quantity)}
                        </p>
                      </>
                    ) : (
                      <p className="text-sm font-bold text-primary">
                        {formatPrice(item.product?.price * item.quantity)}
                      </p>
                    )}
                  </div>

                  {/* Quantity Controls — minus only */}
                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => updateQuantity(item.productId!, item.quantity - 1)}
                    >
                      <Minus className="w-3 h-3" />
                    </Button>
                    <span className="w-6 text-center text-xs font-medium">
                      {item.quantity}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </CardContent>

      <Separator />

      <CardFooter className="flex-col gap-4 p-4">
        {/* Total */}
        <div className="w-full flex items-center justify-between">
          <span className="text-sm text-muted-foreground">{t('store.cart.subtotal')}</span>
          <span className="font-display font-bold text-xl text-primary">
            {formatPrice(subtotal)}
          </span>
        </div>

        {/* Checkout Button */}
        <Button
          className="w-full bg-primary hover:bg-primary-hover text-primary-foreground"
          onClick={() => navigate('/carrinho')}
        >
          {t('store.cart.checkout')}
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </CardFooter>
    </Card>
  );
}
